import type { ReactNode } from "react";
import { forwardRef } from "react";
import type { ButtonProps as ReactAriaButtonProps } from "react-aria-components";
import { Button as ReactAriaButton } from "react-aria-components";

import { cn, cv, type VariantProps } from "../utils";
import { NcLoadingIndicator } from "./nc-loading-indicator";

/* Link classes located here to avoid circular imports */
export const ncLinkClasses = {
  base: [
    "font-normal transition-colors cursor-pointer rounded-none",
    "aria-disabled:text-disabled data-disabled:text-disabled",
    "aria-disabled:cursor-not-allowed data-disabled:cursor-not-allowed",
    "data-focus-visible:outline-active data-focus-visible:outline-2 outline-offset-2",
  ],
  variant: {
    default: [
      "underline text-main",
      "data-hovered:text-active data-hovered:decoration-2",
      "aria-disabled:bg-transparent aria-disabled:no-underline",
      "data-disabled:bg-transparent data-disabled:no-underline",
    ],
  },
};

export const ncButtonClasses = {
  base: [
    "relative inline-flex gap-2 rounded-md cursor-pointer text-center items-center justify-center text-decoration-none font-bold transition-colors",
    "data-disabled:bg-disabled-light aria-disabled:bg-data-disabled-light",
    "data-disabled:text-disabled aria-disabled:text-data-disabled",
    "data-disabled:border-disabled/15 aria-disabled:border-data-disabled-light",
    "data-disabled:cursor-not-allowed aria-disabled:cursor-not-allowed",
    "data-focus-visible:outline-active data-focus-visible:outline-2 outline-offset-2",
    "data-selected:bg-active data-selected:border-active data-selected:text-light",
  ],
  variant: {
    default: [
      "bg-light text-main border-2 border-ui px-4 py-2",
      "data-hovered:bg-main data-hovered:text-light data-hovered:border-main",
      "data-pressed:bg-active data-pressed:text-light data-pressed:border-active",
    ],
    primary: [
      "bg-success text-light border-2 border-success-active px-4 py-2",
      "data-hovered:bg-success-active data-hovered:border-success-active data-hovered:text-light",
      "data-pressed:bg-success data-pressed:border-success",
    ],
    danger: [
      "bg-light text-danger border-2 border-danger-active px-4 py-2",
      "data-hovered:bg-danger data-hovered:text-light data-hovered:border-danger",
      "data-pressed:bg-danger-active data-pressed:text-light data-pressed:border-danger-active",
    ],
    link: [ncLinkClasses.base, ncLinkClasses.variant.default],
    icon: ["bg-transparent text-dark", "data-hovered:text-main", "data-pressed:text-active"],
    iconDanger: [
      "bg-transparent text-danger",
      "data-hovered:text-danger-active",
      "data-pressed:text-danger",
    ],
    trigger: ["font-inherit"],
  },
};

const buttonVariants = cv(ncButtonClasses.base, {
  variants: {
    variant: ncButtonClasses.variant,
  },
  defaultVariants: {
    variant: "default",
  },
});

export interface NcButtonProps extends ReactAriaButtonProps, VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
  children: ReactNode;
}

export const NcButton = forwardRef<HTMLButtonElement, NcButtonProps>(
  (
    { type = "button", variant = "default", isLoading, isDisabled, children, className, ...props },
    ref
  ) => {
    return (
      <ReactAriaButton
        data-nc="NcButton"
        ref={ref}
        type={type}
        className={cn(buttonVariants({ variant }), className)}
        isDisabled={isLoading || isDisabled}
        data-loading={isLoading}
        {...props}
      >
        {children}
        {isLoading && (
          <span className="absolute inset-0 flex items-center justify-center bg-inherit">
            <NcLoadingIndicator />
          </span>
        )}
      </ReactAriaButton>
    );
  }
);
